/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useCallback } from 'react';
import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { IProduct } from '../../../models/standard/ProductModel';

export interface IProductActions {
  addProduct: (productModel: IProduct, spaceUuid?: string) => void;  
  deleteProduct: (uuid: string) => void;
  updateProduct: (updatedProduct: IProduct) => void;
  setProductRotation: (uuid: string, rotation: number) => void;
}

export const useProductActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IProductActions => {
  
// SIMPLIFIED: No fetching here anymore!
const addProduct = useCallback(
  (productModel: IProduct, spaceUuid?: string): void => {
    
    setBuilding((prevBuilding) => {
      const next = { ...prevBuilding };

      // 1. Try Adding to Space
      if (spaceUuid && next.rooms) {
          let addedToSpace = false;
          const newRooms = next.rooms.map(room => {
              if (!room.spaces) return room;
              let roomModified = false;
              const newSpaces = room.spaces.map(space => {
                  if (space.uuid === spaceUuid) {
                      addedToSpace = true;
                      roomModified = true;
                      const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                      newSpace.products = [...(space.products || []), productModel];
                      return newSpace;
                  }
                  return space;
              });
              if (roomModified) {
                  const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
                  newRoom.spaces = newSpaces;
                  return newRoom;
              }
              return room;
          });

          if (addedToSpace) {
              return { ...next, rooms: newRooms, isSetByUndoRedo: false, lastAction: EHistoryAction.PRODUCT_ADDED };
          }
      }

      // 2. Fallback to Root
      next.products = [...prevBuilding.products, productModel];
      return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.PRODUCT_ADDED };
    });
  },
  [setBuilding],
);

  const deleteProduct = useCallback(
    (uuid: string): void => {
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
        let found = false;

        // 1. Root
        if (next.products) {
            const idx = next.products.findIndex(p => p.uuid === uuid);
            if (idx !== -1) {
                const newArr = [...next.products];
                newArr.splice(idx, 1);
                next.products = newArr;
                found = true;
            }
        }

        // 2. Spaces
        if (!found && next.rooms) {
            next.rooms = next.rooms.map(room => {
                if (!room.spaces) return room;
                let roomChanged = false;
                const newSpaces = room.spaces.map(space => {
                    if (!space.products) return space;
                    const idx = space.products.findIndex(p => p.uuid === uuid);
                    if (idx !== -1) {
                        found = true; roomChanged = true;
                        const newArr = [...space.products];
                        newArr.splice(idx, 1);
                        const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                        newSpace.products = newArr;
                        return newSpace;
                    }
                    return space;
                });
                if (roomChanged) {
                    const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
                    newRoom.spaces = newSpaces;
                    return newRoom;
                }
                return room;
            });
        }

        if (!found) return prevBuilding;
        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.PRODUCT_DELETED };
      });
    },
    [setBuilding],
  );

  const updateProduct = useCallback(
    (updatedProduct: IProduct): void => {
      const targetUuid = updatedProduct.uuid;
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
        let found = false;

        // 1. Root
        if (next.products) {
            const idx = next.products.findIndex(p => p.uuid === targetUuid);
            if (idx !== -1) {
                const newArr = [...next.products];
                newArr[idx] = updatedProduct;
                next.products = newArr;
                found = true;
            }
        }

        // 2. Spaces
        if (!found && next.rooms) {
            next.rooms = next.rooms.map(room => {
                if (!room.spaces) return room;
                let roomChanged = false;
                const newSpaces = room.spaces.map(space => {
                    if (!space.products) return space;
                    const idx = space.products.findIndex(p => p.uuid === targetUuid);
                    if (idx !== -1) {
                        found = true; roomChanged = true;
                        const newArr = [...space.products];
                        newArr[idx] = updatedProduct;
                        const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                        newSpace.products = newArr;
                        return newSpace;
                    }
                    return space;
                });
                if (roomChanged) {
                    const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
                    newRoom.spaces = newSpaces;
                    return newRoom;
                }
                return room;
            });
        }

        if (!found) return prevBuilding;
        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.PRODUCT_UPDATED };
      });
    },
    [setBuilding],
  );

  const setProductRotation = useCallback(
    (uuid: string, rotation: number): void => {
      setBuilding((prevBuilding) => {
        const next = { ...prevBuilding };
        let found = false;

        // 1. Root
        if (next.products) {
            const idx = next.products.findIndex(p => p.uuid === uuid);
            if (idx !== -1) {
                const newArr = [...next.products];
                newArr[idx] = { ...newArr[idx], rotation };
                next.products = newArr;
                found = true;
            }
        }

        // 2. Spaces
        if (!found && next.rooms) {
             next.rooms = next.rooms.map(room => {
                if (!room.spaces) return room;
                let roomChanged = false;
                const newSpaces = room.spaces.map(space => {
                    if (!space.products) return space;
                    const idx = space.products.findIndex(p => p.uuid === uuid);
                    if (idx !== -1) {
                        found = true; roomChanged = true;
                        const newArr = [...space.products];
                        newArr[idx] = { ...newArr[idx], rotation };
                        const newSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                        newSpace.products = newArr;
                        return newSpace;
                    }
                    return space;
                });
                if (roomChanged) {
                    const newRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
                    newRoom.spaces = newSpaces;
                    return newRoom;
                }
                return room;
             });
        }

        if (!found) return prevBuilding;
        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.PRODUCT_ROTATION_CHANGED };
      });
    },
    [setBuilding],
  );

  return { addProduct, deleteProduct, updateProduct, setProductRotation };
};